import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Section } from "@/components/Shell";
import { ADMIN } from "@/constants/testIds";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, FileWarning, Clock, CheckCircle2, XCircle, Inbox, Timer } from "lucide-react";

function StatCard({ testId, label, value, icon, tone = "paper" }) {
  const bg =
    tone === "ink"
      ? "bg-[rgb(var(--ink))] text-[rgb(var(--paper))]"
      : tone === "flame"
      ? "bg-[rgb(var(--flame))]"
      : "bg-[rgb(var(--paper))]";
  return (
    <div className={`brut-card p-5 ${bg}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] opacity-80">
          {label}
        </div>
        <div className="opacity-80">{icon}</div>
      </div>
      <div data-testid={testId} className="font-display text-5xl">
        {value}
      </div>
    </div>
  );
}

export default function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, r] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/candidates"),
        ]);
        setStats(s.data);
        setRecent(r.data.slice(0, 5));
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="space-y-10">
      <Section kicker="Overview" title="Field ops pipeline">
        <div className="grid md:grid-cols-4 gap-5">
          <StatCard testId={ADMIN.statTotal} label="Total candidates" value={stats?.total ?? "—"} icon={<Inbox size={16} />} tone="ink" />
          <StatCard testId={ADMIN.statSubmitted} label="Submitted" value={stats?.submitted ?? "—"} icon={<Clock size={16} />} tone="flame" />
          <StatCard testId={ADMIN.statUnderReview} label="Under review" value={stats?.under_review ?? "—"} icon={<Timer size={16} />} />
          <StatCard testId={ADMIN.statApproved} label="Approved" value={stats?.approved ?? "—"} icon={<CheckCircle2 size={16} />} />
          <StatCard testId={ADMIN.statRejected} label="Rejected" value={stats?.rejected ?? "—"} icon={<XCircle size={16} />} />
          <StatCard testId={ADMIN.statDocsPending} label="Docs pending" value={stats?.docs_pending_verification ?? "—"} icon={<Clock size={16} />} />
          <StatCard testId={ADMIN.statDocsExpiring} label="Docs expiring <60d" value={stats?.docs_expiring_soon ?? "—"} icon={<FileWarning size={16} />} tone="flame" />
        </div>
      </Section>

      <Section
        kicker="Recent"
        title="Latest applications"
        right={
          <Link to="/admin/candidates" className="btn-brut btn-ghost text-xs">
            View all <ArrowRight size={14} />
          </Link>
        }
      >
        {loading ? (
          <div className="font-mono text-sm text-black/60">Loading…</div>
        ) : recent.length === 0 ? (
          <div className="brut-card p-8 text-center">
            <div className="font-mono text-xs uppercase tracking-widest text-black/60 mb-1">
              No applications yet
            </div>
            <div>Share your onboarding link with your first crew.</div>
          </div>
        ) : (
          <ul className="brut-card divide-y-2 divide-black">
            {recent.map((c) => (
              <li key={c.id} className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-medium">{c.full_name}</div>
                  <div className="font-mono text-xs text-black/60">
                    {c.reference} · {c.email}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="pill pill-flame">{c.status.replace("_", " ")}</span>
                  <Link
                    to={`/admin/candidates/${c.id}`}
                    className="btn-brut btn-ghost text-xs"
                  >
                    Open <ArrowRight size={12} />
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}
